import memoize from 'lodash/memoize';
import isNumber from 'lodash/isNumber';
import psl from 'psl';
import { getAnalytics, logEvent } from 'firebase/analytics';
import Cookies from 'js-cookie';
import { axiosClient, axiosNuxt } from '~/common/js/axios';
import { wait } from '~/common/js/utils';
import debounce from 'lodash/debounce';
import clone from 'lodash/clone';
import { COOKIE_NAME } from './login-util';
/**
 * 交易类型
 */
export enum TRADE_ENUMS {
  /**
   * 现货
   */
  SPOT,
  /**
   * U本位合约
   */
  CONTRACT_USDT,
  /**
   * 币本位合约
   */
  CONTRACT_COIN,
  /**
   * 杠杆
   */
  MARGIN,
}

export enum PROJECT {
  /**
   * 主站
   */
  MASTER = 0,
  /**
   * 合约
   */
  CONTRACT = 1,
}

export const createHostname = memoize((hostname: string) => {
  if (hostname === 'localhost' || /^[\d.]{1,}$/.test(hostname)) {
    return {
      master: location.origin,
      contract: location.origin,
    };
  }

  const result = psl.parse(hostname);
  const masterSegments = [result.domain];
  const contractSegments = [result.domain];
  if (['beta', 'derivatives-beta'].includes(result.subdomain)) {
    // beta 适配
    masterSegments.unshift('beta');
    contractSegments.unshift('derivatives-beta');
  } else if (['www', 'derivatives'].includes(result.subdomain)) {
    masterSegments.unshift('www');
    contractSegments.unshift('derivatives');
  } else {
    // 本地开发环境
    // eslint-disable-next-line no-lonely-if
    if (result.subdomain.includes('derivatives')) {
      masterSegments.unshift('www');
      contractSegments.unshift(result.subdomain);
    } else {
      masterSegments.unshift(result.subdomain);
      contractSegments.unshift('derivatives');
    }
  }
  let masterHostName = masterSegments.join('.');
  let contractHostName = contractSegments.join('.');
  if (masterHostName === hostname) {
    masterHostName = ``;
    contractHostName = `https://${contractHostName}`;
  } else if (contractHostName === hostname) {
    contractHostName = ``;
    masterHostName = `https://${masterHostName}`;
  }
  return {
    master: masterHostName,
    contract: contractHostName,
  };
});

export const createLink = (project: PROJECT, path: string) => {
  if (process.server) {
    return '';
  }
  const { master, contract } = createHostname(location.hostname);
  switch (project) {
    case PROJECT.MASTER:
      return `${master}${path}`;
    case PROJECT.CONTRACT:
      return `${contract}${path}`;
    default:
      return path;
  }
};

type validateAuthorizeOptionType = {
  callback?: () => void
}
// 需要授权的错误码
export const AUTHORIZE_ERROR_CODE: (string | number)[] = [33138, 34066]; // 34066 -> 理财拉起二维码错误码
export const verifyAuthorizeCode = (store: any, errorCode: number, options: validateAuthorizeOptionType) => {
  if (AUTHORIZE_ERROR_CODE.includes(errorCode)) {
    store.commit('VERIFY_KYC_FLOW_START', {
      startKycLevel: -1,
      callback: options.callback,
    });
    return false;
  }
  return true;
};

interface MessageConfig {
  level: 'success' | 'warning' | 'info' | 'error'
  message: string
  duration?: number
  throttleTime?: number
}

const MessageQueue = new Map<string, boolean>();

export const debounceMessage = (config: MessageConfig) => {
  const route = useRoute();
  // if (!window.$nuxt) {
  //   return;
  // }
  if (route.query?.jumpcodebm) return;
  if (MessageQueue.has(config.message)) {
    return;
  }
  setTimeout(
    () => {
      MessageQueue.delete(config.message);
    },
    isNumber(config.throttleTime) ? config.throttleTime : 5000
  );

  MessageQueue.set(config.message, true);
  ElMessage({
    message: config.message,
    type: config.level,
    duration: isNumber(config.duration) ? config.duration : 3000,
  });
};

type firebaseEventDataType = {
  [key: string]: any
}
export function firebaseLogEvent(name: string, eventData: firebaseEventDataType) {
  if (import.meta.env.VITE_DEBUG !== 'true' && process.client) {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, name, eventData);
    } catch (e) {
      console.log(e);
    }
  }
}

export const openZendesk = async (url: string, isBlank = false, doRedirect = true) => {
  const oriArr = [
    'https://support.bitmart.com',
    'https://support.bmx.fund',
    'https://www.bitmart.com/html/user_zendesk_sso?redirect=',
    '/html/user_zendesk_sso?redirect=',
  ];
  let returnTo = url;
  oriArr.forEach((host) => {
    if (returnTo.startsWith(host)) {
      returnTo = returnTo.replace(host, '');
    }
  });
// TODO 3619
  const token = Cookies.get(COOKIE_NAME.SALT);
  let redirectUrl;
  if (token) {
    try {
      const res = await axiosClient({
        method: 'get',
        url: '/gw-api/user-center/token/zendesk',
        params: {
          returnTo,
        },
        cache: false,
      });
      if (res.success) {
        redirectUrl = res.data.redirectUrl;
      } else {
        // redirectUrl = `https://support.bitmart.com${returnTo}`
        redirectUrl = `${process.env.MASTER_HOST}/html/user_zendesk_sso?redirect=${encodeURIComponent(returnTo)}`;
      }
    } catch (err) {
      // console.log(err)
      redirectUrl = `${process.env.MASTER_HOST}/html/user_zendesk_sso?redirect=${encodeURIComponent(returnTo)}`;
    }
  } else {
    // 未登录
    redirectUrl = `https://support.bitmart.com${returnTo}`;
  }
  if (doRedirect) {
    if (isBlank) {
      window.open(redirectUrl);
    } else {
      location.href = redirectUrl;
    }
  }
  return redirectUrl;
};

interface IViewConfigObjectItem {
  parentKeys: Record<string, number>;
  useParentKeysConfig?: Record<string, number>;
	keys: Record<string, number>;
	useKeysConfig?: Record<string, number>;
  cache: any;
  fetch: Function;
  result?: {
    [key: string]: string;
  }
}
interface IViewConfigObject {
  [key:number|string]:IViewConfigObjectItem
}
const viewConfigObject: IViewConfigObject = {};
const customConfigObject: IViewConfigObject = {};

// 逻辑参考getViewConfigAxios，目前无法进行合并处理
const getCustomConfigAxios = async (configParentKeyItem: IViewConfigObjectItem) => {
	const parentKeys = Object.keys(configParentKeyItem.parentKeys);
	configParentKeyItem.useParentKeysConfig = clone(configParentKeyItem.parentKeys);
	configParentKeyItem.parentKeys = {};
	if (process.client) {
		const res = await axiosClient({
			method: 'POST',
			url: '/gw-api/content/front/lowCode/batchQueryData',
			data: {
				parentKeys,
			},
			headers: {
				'Content-Type': 'application/json',
			},
			cache: configParentKeyItem.cache,
		});
		configParentKeyItem.result = res.data.parentJsonMap;
	} else {
		const res = await axiosNuxt({
			method: 'POST',
			url: '/content/front/lowCode/batchQueryData',
			data: {
				parentKeys,
			},
			headers: {
				'Content-Type': 'application/json',
			},
		});
		configParentKeyItem.result = res.data?.parentJsonMap || {};
	}
};

const getViewConfigAxios = async (configKeyItem:IViewConfigObjectItem) => {
  const keys = clone(Object.keys(configKeyItem.keys));
  configKeyItem.useKeysConfig=clone(configKeyItem.keys);
  configKeyItem.keys = [];
  if (process.client) {

    const res = await axiosClient({
      method: 'POST',
      url: '/gw-api/content/front/lowCode/batchQueryData',
      data: {
        keys,
      },
      headers: {
        'Content-Type': 'application/json',
      },
      cache: configKeyItem.cache,
    });
    configKeyItem.result = res.data.jsonMap;
    // setTimeout(() => delete configKeyItem.result, 40);
  } else {
    const res = await axiosNuxt({
      method: 'POST',
      url: '/content/front/lowCode/batchQueryData',
      data: {
        keys,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
    configKeyItem.result = res.data.jsonMap;
    // setTimeout(() => delete configKeyItem.result, 40);
  }
};

/**
 * 用来获取配置项
 * @param {*} configKey key值
 */
export async function getCustomConfig(configParentKey: string, cacheTtl: boolean | number = 5 * 60 * 1e3) {
	if (!process.client) {
		cacheTtl = false;
	}
	try {
		let cache;
		if (cacheTtl) {
			cache = {
				ttl: cacheTtl,
				methods: ['get', 'post'],
				interpretHeader: false,
			};
		} else {
			cache = false;
		}
		if (!customConfigObject[cacheTtl + '']) {
			customConfigObject[cacheTtl + ''] = {
				parentKeys: {
					[configParentKey]: 1,
				},
				keys: {},
				cache,
				fetch: debounce(getCustomConfigAxios, 50),
			};
			customConfigObject[cacheTtl + ''].fetch(customConfigObject[cacheTtl + '']);
		} else if (!customConfigObject[cacheTtl + ''].parentKeys[configParentKey]) {
			customConfigObject[cacheTtl + ''].parentKeys[configParentKey] = 1;
			if (Object.keys(customConfigObject[cacheTtl + ''].parentKeys).length === 10) {
				// 批量查询接口限制最多10条，立刻触发
				getCustomConfigAxios(customConfigObject[cacheTtl + '']);
			} else {
				customConfigObject[cacheTtl + ''].fetch(customConfigObject[cacheTtl + '']);
			}
		} else if (customConfigObject[cacheTtl + ''].parentKeys[configParentKey]) {
			customConfigObject[cacheTtl + ''].parentKeys[configParentKey] += 1;
		}

		while (!customConfigObject[cacheTtl + ''].result) {
			await wait(10);
		}

		// 这里需要优化
		const res = customConfigObject[cacheTtl + '']?.result;

		if (res) {
			const customKey = Object.keys(res);
			const resData = customKey.reduce((map: Record<string, number>, key: string) => {
				// JSON解析失败时的处理 try catch
				try {
					map[key] = JSON.parse(res[key]);
				} catch (e) {
					console.log('e', e);
				}

				return map;
			}, {});

			if (customConfigObject[cacheTtl + '']) {
				customConfigObject[cacheTtl + ''].useParentKeysConfig[configParentKey] -= 1;
			}
			// if (customConfigObject[cacheTtl + '']?.useParentKeysConfig[configParentKey] === 0) {
			// 	delete customConfigObject[cacheTtl + '']?.result?.[configParentKey];
			// 	if (!Object.keys(customConfigObject[cacheTtl + '']?.result)?.length) {
			// 		delete customConfigObject[cacheTtl + ''];
			// 	}
			// }
			return resData;
		}
		return null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * 用来获取配置项
 * @param {*} configKey key值
 */
export async function getViewConfig(configKey:string, cacheTtl: boolean | number = 5 * 60 * 1e3) {
  if (!process.client) {
    cacheTtl = false;
  }
  try {
    let cache;
    if (cacheTtl) {
      cache = {
        ttl: cacheTtl,
        methods: ['get', 'post'],
        interpretHeader: false,
      };
    } else {
      cache = false;
    }
    if (!viewConfigObject[cacheTtl+'']) {
      viewConfigObject[cacheTtl+''] = {
        keys: {[configKey]:1},
        cache,
        fetch: debounce(getViewConfigAxios, 50),
      };
      viewConfigObject[cacheTtl+''].fetch(viewConfigObject[cacheTtl+'']);
    } else if (!viewConfigObject[cacheTtl+''].keys[configKey]) {
      viewConfigObject[cacheTtl+''].keys[configKey]=1;
      if (Object.keys(viewConfigObject[cacheTtl+''].keys).length === 10) {
        //批量查询接口限制最多10条
        getViewConfigAxios(viewConfigObject[cacheTtl+'']);
      } else {
        viewConfigObject[cacheTtl+''].fetch(viewConfigObject[cacheTtl+'']);
      }
    }else if(viewConfigObject[cacheTtl+''].keys[configKey]){
      viewConfigObject[cacheTtl+''].keys[configKey]+=1;
    }

    while (!viewConfigObject[cacheTtl+'']?.result) {
      await wait(10);
    }
    const res = viewConfigObject[cacheTtl+'']?.result?.[configKey];

    if (res) {
      const retData = JSON.parse(res);
      if (viewConfigObject[cacheTtl+'']) {
        viewConfigObject[cacheTtl+''].useKeysConfig[configKey]-=1;
      }
      if(viewConfigObject[cacheTtl+'']?.useKeysConfig[configKey]===0){
        delete viewConfigObject[cacheTtl+'']?.result?.[configKey];
        if(!Object.keys(viewConfigObject[cacheTtl+'']?.result)?.length){
          delete viewConfigObject[cacheTtl+''];
        }
      }
      return retData;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
}

export const parseUrl = (path: string, _: boolean) => {
  const newPath = `${location.origin}${path}`;
  const url = new URL(newPath);
  const query: any = {};
  url.searchParams.forEach((val: string, key: string) => {
    query[key] = val;
  });
  return {
    url,
    query
  };
};

export const ZENDESK_LANG_MAP = new Map([
  ['cn', 'zh-cn'],
  ['cn_tw', 'zh-tw'],
  ['en', 'en-us'],
  ['es', 'es'],
  ['jp', 'ja'],
  ['br', 'pt'],
  ['tr', 'tr'],
  ['vn', 'vi'],
  ['ru', 'ru'],
  ['nl-NL', 'nl'],
]);

export const breakDownHostUrl = (url: string) => {
  let hostUrl = url;
  let newUrl = hostUrl;
  // 防止未做301跳转，产生XXX.XXX的域名
  if (hostUrl.split('.') && hostUrl.split('.').length <= 2) {
    newUrl = hostUrl;
  } else {
    let number = hostUrl.indexOf('.');
    newUrl = hostUrl.slice(number + 1);
  }
  return newUrl;
};

const LOCAL_DOMAIN_MAP = new Map([
  ['production', 'bitmart.com'],
  ['testing', 'bitmartgcp-test.com'],
  ['dev', 'bitmart-dev.com'],
]);
const LEGACY_TESTING_DOMAIN = 'bitmarttest.com';

export const getCurHost = memoize((origin: string) => {
  let url;
  try {
    url = new URL(origin);
  } catch (error) {
    console.log(111, error)
  }
  const isLocal = process.env.NODE_ENV === 'development';
  const isTest = import.meta.env.VITE_DOMAIN_ENV === 'testing';

  // console.log('url', url);
  let endFix, isBeta, protocol = 'https:', hostFix='com';
  if (url) {
    endFix = breakDownHostUrl(url.host); // bitmartgcp-test.com / bitmartgcp-test.cool / bitmart.com / bitmart.cool
    isBeta = url.hostname.includes('beta.');
    protocol = url.protocol;
  } else {
    endFix = 'bitmart.com';
    isBeta = false;
  }
  const hostFixArr = endFix.split('.');
  hostFix = hostFixArr[hostFixArr.length - 1];
  let contractEndFix = endFix;
  // console.log('contractEndFix', contractEndFix);

  if (isLocal) {
    endFix = LOCAL_DOMAIN_MAP.get(import.meta.env.VITE_DOMAIN_ENV as string) as string;
    if (isTest) {
      contractEndFix = LEGACY_TESTING_DOMAIN;
    } else {
      contractEndFix = endFix;
    }
    protocol = 'https:';
  } else if (isTest) {
    contractEndFix = `bitmarttest.${hostFix}`;
  }

  let siteOrigin;
  if (isBeta) {
    siteOrigin = `${protocol}//beta.${endFix}`; // 主站的 地址
  } else {
    siteOrigin = `${protocol}//www.${endFix}`; // 主站的 地址
  }

  let derivativesOrigin;
  if (isBeta) {
    derivativesOrigin = siteOrigin.replace('beta.', 'derivatives-beta.');
  } else {
    derivativesOrigin = siteOrigin.replace('www.', 'derivatives.');
  }
  const cookieDomain = `.${endFix}`;

  let wssHostV2;
  let contractApiHostV2;
  let contractApiHost;
  let wssHost;
  if (isTest) {
    wssHostV2 = `wss://contract-ws-sandbox.${contractEndFix}`;
    contractApiHostV2 = `${protocol}//contract-sandbox.${contractEndFix}`;
    contractApiHost = `${protocol}//contract.${contractEndFix}`;
    wssHost = `wss://contractapp.${contractEndFix}`;
  } else {
    wssHostV2 = `wss://contract-ws-v2.${contractEndFix}`;
    contractApiHostV2 = `${protocol}//contract-v2.${contractEndFix}`;
    contractApiHost = `${protocol}//contract.${contractEndFix}`;
    wssHost = `wss://contractapp.${contractEndFix}`;
  }

  // 所有的域名集合
  const allOrigin = [...new Set(
    [
      siteOrigin,
      siteOrigin?.replace('.com', '.cool'),
      siteOrigin?.replace('.cool', '.com'),
    ]
  )];

  const findUrlStart = (url: string) => allOrigin.find(() => url.startsWith(origin));

  const siteConfig = {
    origin: siteOrigin,
    derivativesOrigin, // https://derivatives.bitmart.com
    cookieDomain, // .bitmart.com
    isBeta,
    contractApiHost,
    contractApiHostV2,
    wssHost,
    wssHostV2,
    endFix,
    allOrigin,
    findUrlStart,
  };
  if (process.env.ENV !== 'production') {
    console.log('getCurHost', siteConfig);
  }
  return siteConfig;
});
